import { interaction, Stack, YStack, YStackProps } from '@mythical/ui'
import { Tag } from 'app/components/primitive/Tag/Tag'
import { IconLock } from 'app/components/primitive/Icon'
import { timeToSeconds } from 'app/utils/time-to-seconds'
import { SolitoImage } from 'solito/image'
import { SmartLink } from 'app/components/primitive/SmartLink'
import { mapTierToName } from 'app/constants/tiers'

export interface ContentThumbnailProps {
  image: string
  duration?: string
  wrapperProps?: YStackProps
  aspectRatio?: number
  secondsWatched?: number
  href?: string
  tag?: React.ReactNode | string
  isLocked?: boolean
  contentTier?: string | number
  isActive?: boolean
  onPress?: (e: React.BaseSyntheticEvent) => void
  seriesOverlayUrl?: string
  sizes?: string
  priority?: boolean
}

const ContentThumbnail = ({
  wrapperProps,
  duration,
  image,
  aspectRatio = 16 / 9,
  secondsWatched,
  href,
  tag,
  isLocked,
  contentTier,
  isActive,
  onPress,
  seriesOverlayUrl,
  sizes = '(min-width: 768px) 350px, 100vw',
  priority = false,
}: ContentThumbnailProps) => {
  const videoWatchedPercentage =
    secondsWatched &&
    duration &&
    (secondsWatched / timeToSeconds(duration)) * 100

  return (
    <YStack
      w="100%"
      pos="relative"
      br="$2"
      ov="hidden"
      userSelect="none"
      {...wrapperProps}
    >
      <SmartLink
        href={href}
        Component={YStack}
        w="100%"
        animation="medium"
        aspectRatio={aspectRatio}
        backgroundColor="$bgSecondary"
        tabIndex={-1}
        {...((href || onPress) && {
          ...interaction.thumb,
          onPress,
          cursor: 'pointer',
        })}
      >
        <SolitoImage
          src={image}
          fill
          contentFit="cover"
          sizes={sizes}
          alt="Thumbnail"
        />
        {seriesOverlayUrl && (
          <Stack
            pos="absolute"
            top={0}
            left={0}
            w="100%"
            h="100%"
            zi="$0.5"
            pointerEvents="none"
          >
            <SolitoImage
              src={seriesOverlayUrl}
              fill
              contentFit="cover"
              sizes="(min-width: 768px) 350px, 100vw"
              alt="Series overlay"
              priority={priority}
            />
          </Stack>
        )}
      </SmartLink>

      {!!duration && (
        <Tag
          displaySize="sm"
          zIndex="$2"
          variant="neutral"
          position="absolute"
          bottom="$2"
          right="$2"
          pointerEvents="none"
        >
          {duration}
        </Tag>
      )}

      {!isLocked && tag && (
        <Tag
          displaySize="sm"
          variant="primary"
          pos="absolute"
          top="$2"
          left="$2"
          zIndex="$2"
          pointerEvents="none"
        >
          {tag}
        </Tag>
      )}

      {isLocked && (
        <Stack
          position="absolute"
          bottom="$2"
          left="$2"
          zIndex="$2"
          pointerEvents="none"
          bg="$bgTertiary"
          w={20}
          h={20}
          jc="center"
          ai="center"
          br="$pill"
        >
          <IconLock size={10} />
        </Stack>
      )}

      {isLocked && contentTier && (
        <Tag
          displaySize="sm"
          variant="secondary"
          position="absolute"
          top="$2"
          left="$2"
        >
          {mapTierToName(contentTier).name}
        </Tag>
      )}

      {!isLocked && !!videoWatchedPercentage && (
        <Stack
          position="absolute"
          bottom={-1}
          left={0}
          h={4}
          w="100%"
          bg="$bgTertiary"
          zIndex="$1"
          pointerEvents="none"
        >
          <Stack
            position="absolute"
            bottom={0}
            left={0}
            h="100%"
            btrr="$1"
            bbrr="$1"
            w={`${videoWatchedPercentage}%`}
            bg="$fgPrimary"
          />
        </Stack>
      )}

      {isActive && (
        <Stack
          pos="absolute"
          bw={3}
          boc="$fgPrimary"
          top={0}
          left={0}
          w="100%"
          h="100%"
          br="$2"
        />
      )}
    </YStack>
  )
}

export default ContentThumbnail
